import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ChevronUpIcon,
  ExpandMore as ChevronDownIcon,
  Close as RemoveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import type { DashboardWidgetInstance, DashboardTheme, DashboardWidgetType, SidePanelAnchor } from './types';
import { cardStyleFromWidget, contentStyleFromWidget } from './dashboardStyleUtils';

const ALLOWED_CHILD_TYPES: { type: DashboardWidgetType; label: string }[] = [
  { type: 'chart_bar', label: 'Bar chart' },
  { type: 'chart_line', label: 'Line chart' },
  { type: 'chart_area', label: 'Area chart' },
  { type: 'chart_donut', label: 'Donut chart' },
  { type: 'chart_radar', label: 'Radar chart' },
  { type: 'chart_scatter', label: 'Scatter plot' },
  { type: 'chart_treemap', label: 'Treemap' },
  { type: 'gauge', label: 'Gauge' },
  { type: 'indicator', label: 'Indicator' },
  { type: 'data_table', label: 'Data table' },
  { type: 'text', label: 'Text panel' },
  { type: 'calendar_heatmap', label: 'Calendar heatmap' },
  { type: 'iframe', label: 'Embed (iframe)' },
];

export interface DashboardSidePanelProps {
  childWidgetIds: string[];
  anchor: SidePanelAnchor;
  panelWidth: number;
  splitRatios?: number[];
  editMode: boolean;
  theme?: DashboardTheme;
  widgets: Record<string, DashboardWidgetInstance>;
  onUpdatePanel: (patch: { childWidgetIds?: string[]; anchor?: SidePanelAnchor; panelWidth?: number; splitRatios?: number[] }) => void;
  onAddChildWidget: (type: DashboardWidgetType) => string;
  onRemoveChildWidget: (id: string) => void;
  onOpenChildSettings: (id: string) => void;
  renderWidget: (instance: DashboardWidgetInstance, layoutItemId: string) => React.ReactNode;
}

/** Draggable divider between two children inside the side panel. */
function PanelSplitter({
  isHorizontal,
  index,
  ratios,
  onRatiosChange,
  containerRef,
}: {
  isHorizontal: boolean;
  index: number;
  ratios: number[];
  onRatiosChange: (r: number[]) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const dragging = useRef(false);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragging.current = true;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      const container = containerRef.current;
      if (!container) return;

      const move = (ev: PointerEvent) => {
        if (!dragging.current || !container) return;
        const rect = container.getBoundingClientRect();
        const totalSize = isHorizontal ? rect.width : rect.height;
        const pos = isHorizontal ? ev.clientX - rect.left : ev.clientY - rect.top;

        const totalRatio = ratios.reduce((a, b) => a + b, 0);
        const cumBefore = ratios.slice(0, index).reduce((a, b) => a + b, 0);
        const pairSum = ratios[index] + ratios[index + 1];
        const MIN = pairSum * 0.1;
        const newLeft = Math.max(MIN, Math.min(pairSum - MIN, (pos / totalSize) * totalRatio - cumBefore));

        const next = [...ratios];
        next[index] = newLeft;
        next[index + 1] = pairSum - newLeft;
        onRatiosChange(next);
      };

      const up = () => {
        dragging.current = false;
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
      };

      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
    },
    [isHorizontal, index, ratios, onRatiosChange, containerRef],
  );

  return (
    <Box
      onPointerDown={handlePointerDown}
      sx={{
        flexShrink: 0,
        [isHorizontal ? 'width' : 'height']: 2,
        cursor: isHorizontal ? 'col-resize' : 'row-resize',
        bgcolor: 'divider',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'primary.main' },
        zIndex: 5,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          [isHorizontal ? 'top' : 'left']: 0,
          [isHorizontal ? 'bottom' : 'right']: 0,
          [isHorizontal ? 'left' : 'top']: -6,
          [isHorizontal ? 'right' : 'bottom']: -6,
        },
      }}
    />
  );
}

const COLLAPSED_WIDTH = 32;

const DashboardSidePanel: React.FC<DashboardSidePanelProps> = ({
  childWidgetIds,
  anchor,
  panelWidth,
  splitRatios: splitRatiosProp,
  editMode,
  theme,
  widgets,
  onUpdatePanel,
  onAddChildWidget,
  onRemoveChildWidget,
  onOpenChildSettings,
  renderWidget,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const panelBodyRef = useRef<HTMLDivElement | null>(null);
  const ratios =
    splitRatiosProp && splitRatiosProp.length === childWidgetIds.length
      ? splitRatiosProp
      : childWidgetIds.map(() => 1);
  const handleRatiosChange = useCallback(
    (next: number[]) => onUpdatePanel({ splitRatios: next }),
    [onUpdatePanel],
  );

  const isRight = anchor === 'right';
  const isBottom = anchor === 'bottom';
  const isHorizontal = isBottom;

  const handleAddChild = useCallback(
    (type: DashboardWidgetType) => {
      const newId = onAddChildWidget(type);
      onUpdatePanel({ childWidgetIds: [...childWidgetIds, newId] });
      setAddMenuAnchor(null);
    },
    [childWidgetIds, onAddChildWidget, onUpdatePanel],
  );

  const handleRemoveChild = useCallback(
    (childId: string) => {
      onUpdatePanel({ childWidgetIds: childWidgetIds.filter((id) => id !== childId) });
      onRemoveChildWidget(childId);
    },
    [childWidgetIds, onUpdatePanel, onRemoveChildWidget],
  );

  const CollapseToggle = isBottom
    ? (collapsed ? ChevronUpIcon : ChevronDownIcon)
    : isRight
      ? (collapsed ? ChevronLeftIcon : ChevronRightIcon)
      : (collapsed ? ChevronRightIcon : ChevronLeftIcon);

  const sizeProps = isHorizontal
    ? {
        height: collapsed ? COLLAPSED_WIDTH : panelWidth,
        minHeight: collapsed ? COLLAPSED_WIDTH : panelWidth,
        width: '100%',
      }
    : {
        width: collapsed ? COLLAPSED_WIDTH : panelWidth,
        minWidth: collapsed ? COLLAPSED_WIDTH : panelWidth,
        height: '100%',
      };

  return (
    <Box
      sx={{
        ...sizeProps,
        display: 'flex',
        flexDirection: isHorizontal ? 'column' : 'column',
        borderLeft: isRight ? 1 : 0,
        borderRight: anchor === 'left' ? 1 : 0,
        borderTop: isBottom ? 1 : 0,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Collapse toggle bar */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: 0.5,
          py: 0.25,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 36,
          flexShrink: 0,
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {!collapsed && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ pl: 0.5, flex: 1 }}>
            Panel
          </Typography>
        )}
        {!collapsed && editMode && (
          <>
            <Tooltip title="Add widget to panel">
              <IconButton size="small" onClick={(e) => setAddMenuAnchor(e.currentTarget)}>
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
              MenuListProps={{ dense: true }}
            >
              {ALLOWED_CHILD_TYPES.map((t) => (
                <MenuItem key={t.type} onClick={() => handleAddChild(t.type)}>
                  {t.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        <Tooltip title={collapsed ? 'Expand panel' : 'Collapse panel'}>
          <IconButton size="small" onClick={() => setCollapsed((c) => !c)}>
            <CollapseToggle sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Panel body — child widgets */}
      {!collapsed && (
        <Box
          ref={panelBodyRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: isHorizontal ? 'row' : 'column',
          }}
        >
          {childWidgetIds.length === 0 && (
            <Box sx={{ px: 2, py: isHorizontal ? 2 : 4, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.disabled">
                {editMode ? 'Click + to add widgets' : 'No widgets'}
              </Typography>
            </Box>
          )}
          {childWidgetIds.map((childId, idx) => {
            const inst = widgets[childId];
            if (!inst) return null;
            return (
              <React.Fragment key={childId}>
                {idx > 0 && childWidgetIds.length > 1 && (
                  <PanelSplitter
                    isHorizontal={isHorizontal}
                    index={idx - 1}
                    ratios={ratios}
                    onRatiosChange={handleRatiosChange}
                    containerRef={panelBodyRef}
                  />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    flex: ratios[idx],
                    overflow: 'hidden',
                    ...cardStyleFromWidget(inst.config?.style, theme),
                    ...contentStyleFromWidget(inst.config?.style, theme),
                  }}
                >
                  {editMode && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ px: 1, py: 0.25, bgcolor: 'action.selected', flexShrink: 0 }}
                    >
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                        {inst.config?.customTitle || inst.type.replace(/_/g, ' ')}
                      </Typography>
                      <Tooltip title="Widget settings">
                        <IconButton size="small" onClick={() => onOpenChildSettings(childId)} sx={{ p: 0.25 }}>
                          <SettingsIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove from panel">
                        <IconButton size="small" onClick={() => handleRemoveChild(childId)} sx={{ p: 0.25 }}>
                          <RemoveIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', p: 1 }}>
                    {renderWidget(inst, childId)}
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default DashboardSidePanel;
